import React, {useState} from "react";
import {Elements, useStripe} from "@stripe/react-stripe-js";
import {CircularProgress} from "@material-ui/core";
import {Button} from "@mui/material";
import {Currency} from "@evennt-tech/api-v2-core";
import {apiV2, Timestamp} from "api/firebase";
import orderRefs from "api/shows/orders/refs";
import orderService from "api/order";
import {useStripeClientSecret} from "hooks";
import FlexBox from "view/components/Layout/FlexBox";
import {OrderProduct, OrderStatus} from "ts/api/order";
import {resetQuickOrderPaymentAttempt} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/helpers/resetQuickOrderPaymentAttempt";
import {NoAccessWrapper} from "./NoAccessModals";
import useStyles from "./styles";

export function CheckingPaymentStatus() {
  const classes = useStyles();
  return (
    <NoAccessWrapper>
      <FlexBox column alignCenter gap={2}>
        <CircularProgress size={32} />
        <span className={classes.commonText}>
          Checking payment status…
          <br />
          Please, don&apos;t close this page.
        </span>
      </FlexBox>
    </NoAccessWrapper>
  );
}

type ResumeOrRestartPaymentProps = {
  showId: string;
  orderId: string;
  paymentIntentId: string;
  clientSecret: string;
  cardLast4?: string;
  currency: Currency;
  products: OrderProduct[];
  onResolved: (
    nextStatus: OrderStatus.paid | OrderStatus.awaitingPayment
  ) => void;
};

function ResumeOrRestartPaymentChoice({
  showId,
  orderId,
  paymentIntentId,
  clientSecret,
  cardLast4,
  currency,
  products,
  onResolved,
}: ResumeOrRestartPaymentProps) {
  const classes = useStyles();
  const stripe = useStripe();
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState("");

  // Shared by both the "resume failed" and "use a different card" paths: a
  // PaymentIntent that just failed 3DS authentication comes back from Stripe
  // with its payment_method cleared, so `confirmCardPayment(clientSecret)`
  // can never succeed again — the only way forward is a clean restart.
  const restartPayment = async () => {
    await apiV2.oldStripe.cancelPayment({
      paymentIntent: paymentIntentId,
      currency,
      useNewProcess: true,
    });
    await resetQuickOrderPaymentAttempt({showId, orderId});
    onResolved(OrderStatus.awaitingPayment);
  };

  const handleResume = async () => {
    if (!stripe) {
      return;
    }
    setIsWorking(true);
    setError("");
    try {
      const {
        error: stripeError,
        paymentIntent,
      } = await stripe.confirmCardPayment(clientSecret);
      const isSuccessful =
        !stripeError &&
        (paymentIntent?.status === "succeeded" ||
          paymentIntent?.status === "requires_capture");

      if (!isSuccessful) {
        // A payment_method-less PaymentIntent can never succeed via a plain
        // `confirmCardPayment(clientSecret)` retry — that's a full restart.
        // Stripe signals this two different ways depending on how the
        // challenge ended: an explicit issuer-side authentication failure
        // moves the intent to `requires_payment_method` (payment_method
        // detached); abandoning the challenge without an answer instead
        // comes back as an `invalid_request_error` ("...does not have a
        // payment method and none was provided") with no fresh intent state
        // at all. Either way, retrying is pointless. Any other, genuinely
        // transient failure leaves the payment_method attached, so just
        // surface the error and let them try again.
        const paymentMethodGone =
          paymentIntent?.status === "requires_payment_method" ||
          stripeError?.type === "invalid_request_error";
        if (paymentMethodGone) {
          await restartPayment();
          return;
        }

        setError(
          stripeError?.message ??
            "The payment could not be completed. Please try again."
        );
        return;
      }

      await orderRefs.getDocument({showId, orderId}).update({
        "paymentDetails.status": OrderStatus.paid,
        "paymentDetails.paymentDate": Timestamp.now(),
      });
      await orderService.markOrderProductImagesUsed(products);
      onResolved(OrderStatus.paid);
    } catch (e) {
      setError((e as Error)?.message ?? "Something went wrong.");
    } finally {
      setIsWorking(false);
    }
  };

  const handleRestart = async () => {
    setIsWorking(true);
    setError("");
    try {
      await restartPayment();
    } catch (e) {
      setError((e as Error)?.message ?? "Something went wrong.");
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <NoAccessWrapper>
      <span className={classes.wrongUserRole}>
        You have a payment already in progress
      </span>
      <span className={classes.commonText}>
        If you already completed the verification for your card, continue with
        it. Otherwise, you can pay again with a different card.
      </span>
      {!!error && <span className={classes.noLongerValidMessage}>{error}</span>}
      <FlexBox column gap={1} fullWidth>
        <Button
          color="primary"
          variant="contained"
          disabled={isWorking || !stripe}
          onClick={handleResume}
        >
          {cardLast4
            ? `Continue with card •••${cardLast4}`
            : "Continue with previous card"}
        </Button>
        <Button
          color="primary"
          variant="outlined"
          disabled={isWorking}
          onClick={handleRestart}
        >
          Use a different card
        </Button>
      </FlexBox>
    </NoAccessWrapper>
  );
}

export function ResumeOrRestartPayment(props: ResumeOrRestartPaymentProps) {
  const {stripe: ffairStripe, loading} = useStripeClientSecret(props.currency);

  if (loading || !ffairStripe) {
    return <CheckingPaymentStatus />;
  }

  return (
    <Elements stripe={ffairStripe}>
      <ResumeOrRestartPaymentChoice {...props} />
    </Elements>
  );
}
